// src/vatandas/pages/VatandasSurveyPage.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSurveys,
  setError,
  setLoading,
  setSending,
  setSendSuccess,
  setSendError,
  resetSurveyState,
} from "../redux/slices/vatandasSurveySlice";
import { fetchSurveysAPI, sendSurveyResponseAPI } from "../services/vatandasApi";
import "./SurveyPage.css";

import {
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  ChevronUpDownIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/* =============================================================================
  UI Pieces (CreateApplication ile aynı yaklaşım)
============================================================================= */

function Toast({ toast, onClose }) {
  if (!toast) return null;

  const Icon =
    toast.type === "success"
      ? CheckCircleIcon
      : toast.type === "info"
      ? ExclamationTriangleIcon
      : ExclamationTriangleIcon;

  return (
    <div className={`vsv-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon className="vsv-toast-ic" aria-hidden="true" />
      <div className="vsv-toast-body">
        <div className="vsv-toast-title">{toast.title}</div>
        <div className="vsv-toast-text">{toast.message}</div>
      </div>
      <button className="vsv-toast-close" onClick={onClose} aria-label="Kapat">
        <XMarkIcon className="vsv-toast-close-ic" />
      </button>
    </div>
  );
}

function SuccessModal({ open, onClose }) {
  if (!open) return null;

  return (
    <>
      <div className="vsv-modal-overlay" onClick={onClose} />
      <div className="vsv-modal" role="dialog" aria-modal="true" aria-label="Anket gönderildi">
        <div className="vsv-modal-card v-card">
          <div className="vsv-modal-head">
            <div className="vsv-modal-badge" aria-hidden="true">
              <CheckCircleIcon className="vsv-modal-badge-ic" />
            </div>

            <div className="vsv-modal-title-wrap">
              <div className="vsv-modal-title">Teşekkürler</div>
              <div className="vsv-modal-sub">Geri bildiriminiz alındı ve değerlendirmeye alınacaktır.</div>
            </div>

            <button className="vsv-modal-x" onClick={onClose} aria-label="Kapat">
              <XMarkIcon className="vsv-modal-x-ic" />
            </button>
          </div>

          <div className="vsv-modal-actions">
            <button type="button" className="v-btn v-btn--primary" onClick={onClose}>
              Tamam
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* =============================================================================
  Page
============================================================================= */

export default function VatandasSurveyPage() {
  const dispatch = useDispatch();
  const { surveys, loading, error, sending, sendSuccess, sendError } = useSelector(
    (state) => state.vatandasSurvey
  );

  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [answers, setAnswers] = useState({}); // { [questionId]: answerId }
  const [toast, setToast] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);

  const questionRefs = useRef({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toast auto-close
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
  }, []);

  // Fetch surveys (is_active && version == 1)
  useEffect(() => {
    dispatch(setLoading(true));

    fetchSurveysAPI()
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : [];
        const filtered = raw.filter((s) => s?.is_active === true && Number(s?.version) === 1);

        dispatch(setSurveys(filtered));
        setSelectedSurveyId(filtered.length ? Number(filtered[0].id) : null);
      })
      .catch(() => {
        dispatch(setError("Anketler yüklenemedi."));
      });
  }, [dispatch]);

  const selectedSurvey = useMemo(() => {
    if (!selectedSurveyId) return null;
    return surveys.find((s) => Number(s?.id) === Number(selectedSurveyId)) || null;
  }, [surveys, selectedSurveyId]);

  const questions = useMemo(() => selectedSurvey?.questions || [], [selectedSurvey]);

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);
  const totalCount = questions.length;

  const progressPct = useMemo(() => {
    if (!totalCount) return 0;
    return Math.round((answeredCount / totalCount) * 100);
  }, [answeredCount, totalCount]);

  // Survey değişince reset
  useEffect(() => {
    setAnswers({});
    setSubmitAttempted(false);
    setToast(null);
    setShowSuccess(false);
    dispatch(resetSurveyState());
  }, [selectedSurveyId, dispatch]);

  // Redux errors -> toast
  useEffect(() => {
    if (!error) return;
    showToast("error", "Hata", error);
  }, [error, showToast]);

  useEffect(() => {
    if (!sendError) return;
    showToast("error", "Gönderilemedi", sendError);
  }, [sendError, showToast]);

  useEffect(() => {
    if (!sendSuccess) return;
    showToast("success", "Teşekkürler", "Geri bildiriminiz alındı.");
    setShowSuccess(true);
  }, [sendSuccess, showToast]);

  // Success modal auto close (6 sn)
  useEffect(() => {
    if (!showSuccess) return;
    const t = window.setTimeout(() => setShowSuccess(false), 6000);
    return () => window.clearTimeout(t);
  }, [showSuccess]);

  const handleSurveyChange = (e) => {
    const v = Number(e.target.value || 0);
    setSelectedSurveyId(v || null);
  };

  const handleAnswerChange = useCallback((questionId, answerIdRaw) => {
    const answerId = answerIdRaw ? Number(answerIdRaw) : "";
    setAnswers((prev) => ({ ...prev, [questionId]: answerId }));
  }, []);

  const scrollToFirstMissing = useCallback(() => {
    const firstMissing = questions.find((q) => !answers[q.id]);
    if (!firstMissing) return;

    const el = questionRefs.current[firstMissing.id];
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [questions, answers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSurvey || sending) return;

    setSubmitAttempted(true);

    if (answeredCount < totalCount) {
      showToast("info", "Eksik yanıt", "Lütfen tüm soruları yanıtlayınız.");
      scrollToFirstMissing();
      return;
    }

    try {
      dispatch(setSending(true));
      dispatch(setSendError(null));
      dispatch(setSendSuccess(false));

      // Şimdilik: soru soru gönderim (backend batch desteklemiyorsa güvenli)
      for (const question of questions) {
        const payload = {
          survey: selectedSurvey.id,
          question: question.id,
          answer: answers[question.id],
        };
        await sendSurveyResponseAPI(payload);
      }

      dispatch(setSendSuccess(true));
      setAnswers({});
      setSubmitAttempted(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      dispatch(setSendError("Cevaplar gönderilemedi."));
    } finally {
      dispatch(setSending(false));
    }
  };

  return (
    <div className="survey-page vsv-page">
      <div className="survey-container vsv-shell">
        {/* Header (CreateApplication ile aynı mantık) */}
        <div className="vsv-head v-card vsv-head--simple">
          <div className="vsv-head-badge" aria-hidden="true">
            <ClipboardDocumentListIcon className="vsv-head-badge-ic" />
          </div>

          <div className="vsv-head-text">
            <h1 className="vsv-title vsv-title--simple">
              {selectedSurvey?.name || "Memnuniyet Anketi"}
            </h1>
            <div className="vsv-sub">
              Hizmet kalitesini geliştirmek için görüşlerinizi topluyoruz.
            </div>

            <div className="vsv-meta-row">
              <div className="vsv-pill">
                <ShieldCheckIcon className="vsv-pill-ic" aria-hidden="true" />
                <span>Yanıtlar gizli değerlendirilir</span>
              </div>

              <div className="vsv-count" aria-label="İlerleme">
                {answeredCount}/{totalCount}
              </div>
            </div>

            <div className="vsv-progress" aria-label="İlerleme çubuğu">
              <div className="vsv-progress-bar" style={{ width: `${progressPct}%` }} />
            </div>

            {/* Birden fazla anket varsa seçtir */}
            {Array.isArray(surveys) && surveys.length > 1 ? (
              <div className="vsv-survey-select">
                <label className="vsv-label">Anket Seçimi</label>
                <div className="vsv-select-wrap">
                  <select
                    className="vsv-select"
                    value={selectedSurveyId || ""}
                    onChange={handleSurveyChange}
                    disabled={loading || sending}
                  >
                    {surveys.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <ChevronUpDownIcon className="vsv-select-chevron" aria-hidden="true" />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* States */}
        {loading ? (
          <div className="vsv-state v-card">
            <ArrowPathIcon className="vsv-state-ic spin" aria-hidden="true" />
            <div className="vsv-state-title">Anketler yükleniyor</div>
            <div className="vsv-state-text">Lütfen bekleyin…</div>
          </div>
        ) : !selectedSurvey ? (
          <div className="vsv-state v-card">
            <ExclamationTriangleIcon className="vsv-state-ic" aria-hidden="true" />
            <div className="vsv-state-title">Aktif anket bulunamadı</div>
            <div className="vsv-state-text">Şu anda yayınlanan bir anket bulunmamaktadır.</div>
          </div>
        ) : (
          <form className="vsv-form" onSubmit={handleSubmit} noValidate>
            {/* Questions */}
            <div className="vsv-list">
              {questions.map((q, idx) => {
                const isMissing = submitAttempted && !answers[q.id];

                return (
                  <div
                    key={q.id}
                    className="survey-qcard vsv-qcard v-card"
                    ref={(el) => {
                      questionRefs.current[q.id] = el;
                    }}
                    data-missing={isMissing ? "1" : "0"}
                  >
                    <div className="vsv-qtitle">
                      {idx + 1}. {q.text}
                      {isMissing ? <span className="vsv-missing"> *</span> : null}
                    </div>

                    <div className="survey-select-wrap vsv-select-wrap">
                      <select
                        className="survey-select vsv-select"
                        value={answers[q.id] || ""}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        disabled={sending}
                        aria-invalid={isMissing ? "true" : "false"}
                        required
                      >
                        <option value="">Seçiniz</option>
                        {(q.answer_choices || []).map((ac) => (
                          <option key={ac.id} value={ac.id}>
                            {ac.text}
                          </option>
                        ))}
                      </select>

                      <ChevronUpDownIcon className="survey-select-chevron vsv-select-chevron" aria-hidden="true" />

                      {isMissing ? (
                        <div className="vsv-missing-help">Bu soru zorunludur.</div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions (CreateApplication tarzı) */}
            <div className="vsv-actions v-card">
              <div className="vsv-actions-left">
                <div className="vsv-actions-title">
                  <ShieldCheckIcon className="vsv-actions-ic" aria-hidden="true" />
                  <span>Gönderim</span>
                </div>
                <div className="vsv-actions-sub">Tüm soruları yanıtladıktan sonra gönderebilirsiniz.</div>
              </div>

              <div className="vsv-actions-right">
                <button type="submit" className="v-btn v-btn--primary vsv-submit" disabled={sending}>
                  {sending ? (
                    <>
                      <ArrowPathIcon className="vsv-submit-ic spin" aria-hidden="true" />
                      Gönderiliyor…
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="vsv-submit-ic" aria-hidden="true" />
                      Gönder
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Toast */}
        <Toast toast={toast} onClose={() => setToast(null)} />

        {/* Success modal */}
        <SuccessModal open={showSuccess} onClose={() => setShowSuccess(false)} />
      </div>
    </div>
  );
}
